import { useMemo, useSyncExternalStore } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { useCostPriceCategoriesStore } from '../../stores/appCostPriceCategoriesStore';
import { useCostPriceStore } from '../../stores/appCostPriceStore';
import { useFiltersStore } from '../../stores/appFiltersStore';
import { useOrderHistory } from '../../stores/appOrderHistoryStore';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { useRevenueHistory } from '../../stores/appRevenueHistoryStore';
import { useTimeIntervalStore } from '../../stores/appTimeIntervalStore';
import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore';
import { semiBoldText } from '../../styling/stylingHelper';
import { OnboardingIllustrationView } from './illustrations';
import type { OnboardingStep } from './types';
import { subscribeInventoryPractice, inventoryPracticeRevision } from './practice/inventoryPracticeData';
import { subscribeUtilizationPractice, utilizationPracticeRevision } from './practice/utilizationPractice';
import { onPracticeDataChange, practiceRevision } from './practice/practiceGuards';
import { leakageTourRevision, subscribeLeakageTourValues } from './inventoryValues';
import { useTourTableRevision } from './tableExercises';

const NO_VALUES: Record<string, string> = {};

/**
 * `step.values()` re-filters the whole order and revenue history through
 * `readTourSnapshot`, and the overlay re-renders on every measured rect — a drawer
 * sliding open would otherwise cost several full scans. Subscribe to exactly the
 * slices that snapshot reads and recompute only when one of them changes, which also
 * refreshes the sentence mid-step when the user changes a filter or the date range.
 */
function useStepValues(step: OnboardingStep): Record<string, string> {
  const values = step.values;
  const tableRevision = useTourTableRevision();
  const inventoryRevision = useSyncExternalStore(subscribeInventoryPractice, inventoryPracticeRevision, inventoryPracticeRevision);
  const leakageRevision = useSyncExternalStore(subscribeLeakageTourValues, leakageTourRevision, leakageTourRevision);
  const utilizationRevision = useSyncExternalStore(subscribeUtilizationPractice, utilizationPracticeRevision, utilizationPracticeRevision);
  const fixtureRevision = useSyncExternalStore(onPracticeDataChange, practiceRevision, practiceRevision);
  // The getters translate their own labels, so a mid-tour language switch must recompute.
  const { i18n } = useTranslation();
  const orderHistoryList = useOrderHistory(state => state.orderHistoryList);
  const revenueHistoryList = useRevenueHistory(state => state.revenueHistoryList);
  const restaurant = useAppUserDefaultStore(state => state.restaurant);
  const showExVat = useAppUserDefaultStore(state => state.showExVat);
  const showExVatOrders = useAppUserDefaultStore(state => state.showExVatOrders);
  const overallTypeFilter = useFiltersStore(state => state.overallTypeFilter);
  const restaurantFilters = useFiltersStore(state => state.restaurantFilters);
  const startDate = useTimeIntervalStore(state => state.startDate);
  const endDate = useTimeIntervalStore(state => state.endDate);
  const view = useTimeIntervalStore(state => state.view);
  const restaurantRevenueSources = useRestaurantStore(state => state.restaurantRevenueSources);
  const restaurants = useRestaurantStore(state => state.restaurants);
  const categories = useCostPriceCategoriesStore(state => state.categories);
  const menuCategoryFilters = useFiltersStore(state => state.menuCategoryFilters);
  const revenueCategoryFilters = useFiltersStore(state => state.revenueCategoryFilters);
  const revenueSourceFilters = useFiltersStore(state => state.revenueSourceFilters);
  const sectionFilters = useFiltersStore(state => state.sectionFilters);
  const platformFilters = useFiltersStore(state => state.platformFilters);
  const categoryFilters = useFiltersStore(state => state.categoryFilters);
  const costPriceList = useCostPriceStore(state => state.costPriceList);
  return useMemo(
    () => values?.() ?? NO_VALUES,
    // The getter takes no arguments; these are the store slices it reads behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      step.id, values, tableRevision, inventoryRevision, leakageRevision, utilizationRevision, fixtureRevision, orderHistoryList, revenueHistoryList, restaurant, showExVat, showExVatOrders,
      overallTypeFilter, restaurantFilters, startDate, endDate, view, restaurantRevenueSources,
      restaurants, costPriceList, categories, menuCategoryFilters, revenueCategoryFilters,
      revenueSourceFilters, sectionFilters, platformFilters, categoryFilters, i18n.language,
    ]
  );
}

interface OnboardingCardProps {
  step: OnboardingStep;
  onPrimary: () => void;
  onSecondary: () => void;
  onBack?: () => void;
  actionCompleted?: boolean;
  onTaskStart?: (branch: string) => void;
  /** Height budget from the placement engine — see `Placement.cardMaxHeight`. */
  maxHeight?: number;
  targetUnavailable?: boolean;
}

/**
 * One type scale for every layout. Desktop matches Figma; phones step the type
 * and padding down so a 405px card still leaves 16px of air on each side.
 */
const titleClass =
  'text-left font-sans text-[20px] font-semibold leading-[30px] text-textPrimary dark:text-textPrimary-dark max-[640px]:text-[17px] max-[640px]:leading-[25px]';
const descriptionClass =
  'text-left font-sans text-[16px] font-medium leading-6 text-[#4F4F4F] dark:text-[#D0D5DD] max-[640px]:text-[14px] max-[640px]:leading-[21px]';

const heroSecondaryButtonClass =
  'box-border flex h-11 flex-1 items-center justify-center gap-1 rounded-lg border border-borderPrimary bg-card px-3.5 py-2.5 font-sans text-sm font-semibold leading-5 text-textSecondary shadow-[0px_1px_2px_rgba(16,24,40,0.05)] dark:shadow-black/20 dark:border-borderPrimary-dark dark:bg-card-dark dark:text-textSecondary-dark max-[640px]:h-10 max-[640px]:text-[13px]';
const compactSecondaryButtonClass =
  'box-border flex h-10 items-center justify-center gap-1 rounded-lg px-3.5 py-2.5 font-sans text-sm font-semibold leading-5 text-textTertiary dark:text-textTertiary-dark max-[640px]:h-9 max-[640px]:px-2.5 max-[640px]:text-[13px]';
const primaryButtonClass =
  'box-border flex h-11 items-center justify-center gap-3 rounded-xl px-6 py-2.5 font-sans text-sm font-semibold leading-6 text-white [background:linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_100%),#0E8965] [box-shadow:0px_1px_2px_rgba(14,137,101,0.48),0px_0px_0px_1px_#0E8965] max-[640px]:h-10 max-[640px]:px-4 max-[640px]:text-[13px]';

/** `100vw-32px` mirrors the 16px viewport margin the placement engine reserves. */
const cardWidthClass = 'w-[min(calc(100vw-32px),405px)]';
const chooserWidthClass = 'w-[min(calc(100vw-32px),452px)]';

const bodyClass = 'flex min-h-0 flex-1 flex-col gap-5 p-5 max-[640px]:p-4';
const copyClass = 'flex min-w-0 flex-1 flex-col items-start gap-2 text-left';

/** Peach header treatment. 250px on desktop; shrinks on a short phone before text does. */
const PEACH_SPECS: Partial<
  Record<
    NonNullable<OnboardingStep['illustration']>,
    {
      src?: string;
      size?: number;
      width?: number;
      height?: number;
      maxHeight?: number;
      headerHeight?: number;
      empty?: boolean;
      align?: 'center' | 'end';
    }
  >
> = {
  spend: { src: '/assets/money.png', size: 213 },
  cost: { empty: true },
  category: { src: '/assets/empty.svg', align: 'end' },
  complete: { empty: true },
  welcomeRevenue: { size: 248 },
  performance: { size: 248 },
  welcomeUtilization: { src: '/assets/manage.png', size: 248 },
  utilizationTasks: { src: '/assets/tasks.png', size: 248 },
  welcomeInventory: { src: '/assets/inventory-wel.png', size: 248 },
  completeInventory: { src: '/assets/inventory-end.png', size: 248 },
  welcomeLeakage: { src: '/assets/leakage.png', size: 248 },
  leakageJars: { src: '/assets/jars.png', size: 220 },
  completeLeakage: { src: '/assets/leakage-end.png', width: 360, height: 196 },
  inventoryCountBox: { src: '/assets/vegs.png', size: 180 },
  file: { src: '/assets/file.png', size: 248 },
  weightScale: { src: '/assets/weight-scale.png', size: 248 },
  completeRevenue: { empty: true },
  ordersWelcome: { empty: true },
  ordersKpis: { src: '/assets/monitor.png', width: 375, height: 154 },
  ordersSeeAll: { src: '/assets/empty.svg', align: 'end' },
  completeOrders: { empty: true },
  welcomeCost: { empty: true },
  costStandout: { empty: true, headerHeight: 200 },
  costMadeInHouse: { empty: true },
  completeCost: { empty: true },
};

export function OnboardingCard({ step, onPrimary, onSecondary, onBack, onTaskStart, maxHeight, targetUnavailable, actionCompleted }: OnboardingCardProps) {
  const { t } = useTranslation();
  const values = useStepValues(step);
  const isHero = step.layout === 'hero' || step.layout === 'complete';
  const shortCard = maxHeight !== undefined && maxHeight < 360;
  const isCompact = step.layout === 'compact' || shortCard;
  const isChooser = step.layout === 'chooser';
  const isExplore = step.illustration === 'explore';
  const peach = step.illustration ? PEACH_SPECS[step.illustration] : undefined;
  const isPeachTall = Boolean(peach);
  const illustrationFillsHeader = !shortCard && (step.layout === 'spotlight' || isHero);
  /**
   * An action step is finished by doing the thing — clicking the target, or satisfying
   * `advanceWhen` — not by pressing Next, so it ships no primary button. The exception
   * is an unavailable target or an action already completed before pressing Back.
   */
  const isActionStep = Boolean(step.advanceOnTargetClick || step.advanceWhen);
  const hasSecondary = Boolean(step.secondaryLabelKey) && (!isActionStep || Boolean(targetUnavailable));
  const showPrimary = !isActionStep || Boolean(targetUnavailable) || Boolean(actionCompleted);
  const wideActions = isHero;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      aria-describedby="onboarding-description"
      tabIndex={-1}
      className={cn(
        'outline-none flex max-h-[calc(100dvh_-_32px)] flex-col overflow-hidden bg-card dark:bg-card-dark',
        isChooser ? chooserWidthClass : cardWidthClass,
        isExplore && 'rounded-[12px] border border-[#F8F8F8] dark:border-borderPrimary-dark shadow-[0px_8px_24px_-4px_rgba(0,0,0,0.12)] dark:shadow-black/30',
        (isCompact || isChooser) && 'rounded-[12px] shadow-2xl',
        !isExplore && !isCompact && !isChooser && 'rounded-xl shadow-2xl'
      )}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {onBack && (
        <div className="shrink-0 px-2 pt-2">
          <button type="button" onClick={onBack} className={compactSecondaryButtonClass}>
            {t('onboarding.back')}
          </button>
        </div>
      )}
      {step.illustration && illustrationFillsHeader && (
        <div
          className={cn(
            'flex w-full min-h-[104px] shrink items-center justify-center overflow-hidden bg-[#FFF1E8] dark:bg-backgroundSecondary-dark',
            isExplore && 'h-[250px] items-end px-5 pb-5 max-[640px]:h-[168px] max-[640px]:px-4 max-[640px]:pb-4',
            isPeachTall &&
              cn(
                peach?.headerHeight ? '' : 'h-[250px] max-[640px]:h-[168px]',
                peach?.align === 'end' ? 'items-end' : 'items-center'
              ),
            !isExplore && !isPeachTall && (isHero ? 'h-[250px] max-[640px]:h-[168px]' : 'h-36 max-[640px]:h-28')
          )}
          style={peach?.headerHeight ? { height: peach.headerHeight } : undefined}
        >
          {isExplore ? (
            <img
              src="/assets/explore.png"
              alt=""
              className="block h-[148px] max-h-full w-[223px] max-w-full object-contain"
            />
          ) : peach?.empty ? null : peach?.src ? (
            <img
              src={peach.src}
              alt=""
              className={cn('block max-h-full object-contain', peach.size ? 'shrink' : 'max-w-[90%] w-auto')}
              style={
                peach.size
                  ? { width: peach.size, height: peach.size, maxWidth: '100%' }
                  : peach.width || peach.height
                    ? { width: peach.width, height: peach.height, maxWidth: '90%' }
                    : peach.maxHeight
                      ? { maxHeight: peach.maxHeight }
                      : undefined
              }
            />
          ) : peach?.size && step.illustration ? (
            <div className="max-h-full max-w-full" style={{ width: peach.size, height: peach.size }}>
              <OnboardingIllustrationView name={step.illustration} />
            </div>
          ) : (
            <OnboardingIllustrationView name={step.illustration} />
          )}
        </div>
      )}

      {targetUnavailable && (
        <p role="status" className="shrink-0 px-5 pt-4 text-sm text-textSecondary dark:text-textSecondary-dark">
          {t('onboarding.targetUnavailable')}
        </p>
      )}
      {isChooser ? (
        <div className={bodyClass}>
          <div className="flex min-h-0 flex-col gap-5 overflow-y-auto">
          <div className={copyClass}>
            <h2 id="onboarding-title" className={titleClass}>
              {t(step.titleKey, values)}
            </h2>
            <p id="onboarding-description" className={descriptionClass}>
              <Trans
                i18nKey={step.descriptionKey}
                values={values}
                components={{
                  bold: (
                    <strong className={cn(semiBoldText, 'text-textPrimary dark:text-textPrimary-dark')} />
                  ),
                }}
              />
            </p>
          </div>
          {step.chooserTasks && step.chooserTasks.length > 0 && (
            <div className="flex w-full items-start gap-5 max-[640px]:flex-col max-[640px]:gap-3">
              {step.chooserTasks.map(task => (
                <div
                  key={task.titleKey}
                  className="flex min-w-0 flex-1 flex-col items-center gap-3 rounded-lg border border-[#ECECEC] p-4 max-[640px]:flex-row max-[640px]:items-center max-[640px]:p-3 dark:border-borderGrey-dark"
                >
                  <div className="flex h-[73px] w-[73px] shrink-0 items-center justify-center rounded-full bg-[#EDFCF5] max-[640px]:h-[60px] max-[640px]:w-[60px] dark:bg-cardLightGreen-dark">
                    <OnboardingIllustrationView
                      name={task.illustration}
                      compact
                      className="h-[42px] w-[42px] max-[640px]:h-9 max-[640px]:w-9"
                    />
                  </div>
                  <div className="flex min-w-0 w-full flex-1 flex-col items-center gap-1 max-[640px]:items-start">
                    <p className="w-full text-center font-sans text-[14px] font-semibold leading-[22px] text-textPrimary max-[640px]:text-left dark:text-textPrimary-dark">
                      {t(task.titleKey)}
                    </p>
                    <p className="w-full text-center font-sans text-[12px] font-medium leading-[18px] text-[#4F4F4F] max-[640px]:text-left dark:text-[#D0D5DD]">
                      {t(task.descriptionKey)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => (task.branch && onTaskStart ? onTaskStart(task.branch) : onPrimary())}
                    className="box-border flex h-11 w-full items-center justify-center rounded-lg border border-borderPrimary bg-card px-3.5 py-2.5 font-sans text-sm font-semibold leading-5 text-textSecondary shadow-[0px_1px_2px_rgba(16,24,40,0.05)] dark:shadow-black/20 max-[640px]:h-10 max-[640px]:w-auto max-[640px]:shrink-0 dark:border-borderPrimary-dark dark:bg-card-dark dark:text-textSecondary-dark"
                  >
                    {t(task.ctaKey)}
                  </button>
                </div>
              ))}
            </div>
          )}
          </div>
          {(hasSecondary || showPrimary) && (
            <div className="flex shrink-0 flex-col gap-3">
              {hasSecondary && step.secondaryLabelKey && (
                <button type="button" onClick={onSecondary} className={compactSecondaryButtonClass}>
                  {t(step.secondaryLabelKey)}
                </button>
              )}
              {showPrimary && (
                <button type="button" onClick={onPrimary} className={cn(primaryButtonClass, 'h-11 w-full')}>
                  {t(step.primaryLabelKey)}
                </button>
              )}
            </div>
          )}
        </div>

      ) : (
        <div className={bodyClass}>
          <div className="flex min-h-0 w-full items-start gap-4 overflow-y-auto max-[640px]:gap-3">
            {isCompact && step.illustration && (
              <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center max-[640px]:h-12 max-[640px]:w-12">
                <OnboardingIllustrationView name={step.illustration} compact />
              </div>
            )}
            <div className={copyClass}>
              <h2 id="onboarding-title" className={titleClass}>
                {t(step.titleKey, values)}
              </h2>
              <p id="onboarding-description" className={descriptionClass}>
                <Trans
                  i18nKey={step.descriptionKey}
                  values={values}
                  components={{
                    bold: (
                      <strong className={cn(semiBoldText, 'text-textPrimary dark:text-textPrimary-dark')} />
                    ),
                  }}
                />
              </p>
            </div>
          </div>
          {(hasSecondary || showPrimary) && (
            <div className={cn('flex shrink-0 items-center gap-3', wideActions ? 'justify-center' : 'justify-end')}>
              {hasSecondary && step.secondaryLabelKey && (
                <button
                  type="button"
                  onClick={onSecondary}
                  className={cn(wideActions ? heroSecondaryButtonClass : compactSecondaryButtonClass, wideActions && 'flex-1')}
                >
                  {t(step.secondaryLabelKey)}
                </button>
              )}
              {showPrimary && (
                <button
                  type="button"
                  onClick={onPrimary}
                  className={cn(primaryButtonClass, wideActions && 'flex-1')}
                >
                  {t(step.primaryLabelKey)}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
